import rison from 'rison';

import type { IntegrationCategory } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import { ensureIsDefined, filterOutNullable, findInArray, isInArray } from '@gitbook/util-ts';

import { ensureSpaceScreenMode } from './normalize';
import {
    parseIntegrationCategoryParam,
    parseIntegrationModalStateParam,
    parseNavigationIntegrationsProviderParam,
} from './parse-url-params';
import { NavigationAccountDeveloperTabSchema, NavigationBillingQueryParams } from './schema';
import {
    COLLECTION_SIDESHEET_KEYS,
    getNavigationSideSheetTabs,
    getSpaceSideSheetKeys,
    type NavigationChangeRequestsSideSheet,
    type NavigationCommentsSideSheet,
    type NavigationSideSheetType,
} from './side-sheets';
import {
    type LocationDescriptor,
    type NavigationScreenAccountSettings,
    type NavigationScreenAgentState,
    type NavigationScreenAgentsState,
    type NavigationScreenChangeRequestsFilters,
    type NavigationScreenChangeRequestsState,
    type NavigationScreenCollectionState,
    type NavigationScreenContentGapsState,
    type NavigationScreenLinkAuditState,
    type NavigationScreenLegacyIntegrationsState,
    type NavigationScreenOrganizationContentState,
    type NavigationScreenOrgSettings,
    type NavigationScreenSiteFindingsDateRange,
    NavigationScreenSiteFindingsDateRanges,
    type NavigationScreenSiteState,
    type NavigationScreenSitesListState,
    type NavigationScreenSpaceState,
    type NavigationScreenState,
    type NavigationScreenStyleguideNewState,
    type NavigationScreenStyleguidesState,
    type NavigationScreenTriageState,
    NavigationSiteCustomizationTabs,
    NavigationSiteInsightsReports,
    NavigationSiteSettingsSections,
} from './types';
import { normalizePath, type PathMatch, parseRevision, switcher } from './utils';

// Regex to match a page
export const MATCH_PAGE = '([^@~]*)';

interface ParsedLocationDescriptor {
    pathname: string;
    query: URLSearchParams;
}

/**
 * Compute the navigation state from a location.
 */
export function resolveLocationToNavigationScreen(
    location: LocationDescriptor
): NavigationScreenState {
    const query = new URLSearchParams(location.search);

    const screen = { type: 'home' } as NavigationScreenState;
    screenSwitcher(screen, { pathname: normalizePath(location.pathname), query });

    if (location.hash) {
        // Location may include the leading # sometimes
        screen.hash = location.hash.replace(/^#/, '');
    }

    return { ...screen, ...extractGlobalParamsFromLocation(query) };
}

/**
 * Extract some global params from the query.
 */
function extractGlobalParamsFromLocation(
    queryParameters: URLSearchParams
): Pick<NavigationScreenState, 'askAI' | 'coupon' | 'query' | 'ssoLogin'> {
    const no_sso = queryParameters.get('no_sso');
    const email = queryParameters.get('email');
    const ask_ai = queryParameters.get('ask_ai');
    const coupon = queryParameters.get('coupon');
    return {
        query: queryParameters.has('q') ? queryParameters.get('q') || '' : undefined,
        askAI: ask_ai ? ['true', '1'].includes(ask_ai) || undefined : undefined,
        coupon: coupon || undefined,
        ssoLogin:
            no_sso || email
                ? {
                      noSSO: no_sso === 'account_exists' ? no_sso : undefined,
                      email: email || undefined,
                  }
                : undefined,
    };
}

function getSitesParams(location: ParsedLocationDescriptor) {
    return {
        type: 'sites' as const,
        /** Site published status. */
        status: findInArray(location.query.get('status'), ['published', 'unpublished'] as const),
        /** Search query. */
        search: location.query.get('search') || undefined,
    };
}

/**
 * Sub-switcher to match the inbox state. Mutates the given context
 */
function screenSwitcher(screen: NavigationScreenState, location: ParsedLocationDescriptor): void {
    switcher(location.pathname, [
        // Login with basename
        {
            path: '/@:basename/',
            onMatch: (submatch: PathMatch<{ basename: string }>) => {
                Object.assign(screen, {
                    type: 'org_with_basename',
                    organizationBaseName: submatch.params.basename,
                });
            },
        },
        {
            path: '/login/:basename',
            onMatch: (submatch: PathMatch<{ basename: string }>) => {
                Object.assign(screen, {
                    type: 'org_with_basename',
                    organizationBaseName: submatch.params.basename,
                });
            },
        },
        {
            path: '/oauth/authorize',
            exact: true,
            onMatch: () => {
                const signup = location.query.get('signup');
                Object.assign(screen, {
                    type: 'oauth_authorize',
                    sessionId: location.query.get('sessionId') || undefined,
                    signup: signup ? ['true', '1'].includes(signup) || undefined : undefined,
                });
            },
        },
        // Invites
        {
            path: '/invite/:org/:invite',
            onMatch: (match: PathMatch<{ org: string; invite: string }>) => {
                Object.assign(screen, {
                    type: 'org_join',
                    organization: match.params.org,
                    invite: match.params.invite,
                });
            },
        },
        {
            path: '/account',
            onMatch: (submatch) => {
                Object.assign(screen, {
                    type: 'account',
                    tab: 'profile',
                });
                accountSettingsSwitcher(
                    screen as NavigationScreenAccountSettings,
                    submatch,
                    location
                );
            },
        },
        {
            path: '/u/:user',
            onMatch: (submatch: PathMatch<{ user: string }>) => {
                Object.assign(screen, {
                    type: 'user',
                    user: submatch.params.user,
                });
            },
        },
        {
            path: '/integrations/:integration',
            onMatch: (submatch: PathMatch<{ integration: string }>) => {
                const tab: Extract<NavigationScreenOrgSettings['tab'], { type: 'integrations' }> = {
                    type: 'integrations',
                    integration: submatch.params.integration,
                    tab: 'about',
                };
                integrationSettingsTabSwitcher(tab, submatch, location);

                const legacyScreen: NavigationScreenLegacyIntegrationsState = {
                    type: 'integrations_legacy',
                    integration: tab.integration,
                    tab: tab.tab,
                    installation: tab.installation,
                };
                Object.assign(screen, legacyScreen);
            },
        },
        {
            path: '/integrations',
            onMatch: () => {
                const legacyScreen: NavigationScreenLegacyIntegrationsState = {
                    type: 'integrations_legacy',
                    category: parseIntegrationCategoryParam(location.query.get('category')),
                    search: location.query.get('search') || undefined,
                    provider: parseNavigationIntegrationsProviderParam(
                        location.query.get('provider')
                    ),
                };
                Object.assign(screen, legacyScreen);
            },
        },
        {
            path: '/o/:org/integrations/:integration',
            onMatch: (submatch: PathMatch<{ org: string; integration: string }>) => {
                const tab: Extract<NavigationScreenOrgSettings['tab'], { type: 'integrations' }> = {
                    type: 'integrations',
                    integration: submatch.params.integration,
                    tab: 'about',
                };
                integrationSettingsTabSwitcher(tab, submatch, location);

                const orgSettingsScreen: NavigationScreenOrgSettings = {
                    type: 'org_settings',
                    organization: submatch.params.org,
                    tab,
                };
                Object.assign(screen, orgSettingsScreen);
            },
        },
        {
            path: '/o/:org/integrations',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const orgSettingsScreen: NavigationScreenOrgSettings = {
                    type: 'org_settings',
                    organization: submatch.params.org,
                    tab: {
                        type: 'integrations',
                        category: parseIntegrationCategoryParam(location.query.get('category')),
                        search: location.query.get('search') || undefined,
                        provider: parseNavigationIntegrationsProviderParam(
                            location.query.get('provider')
                        ),
                    },
                };
                Object.assign(screen, orgSettingsScreen);
            },
        },
        {
            path: '/o/:org/settings',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const baseScreen: NavigationScreenOrgSettings = {
                    type: 'org_settings',
                    tab: { type: 'profile' },
                    organization: submatch.params.org,
                };

                Object.assign(screen, baseScreen);
                orgSettingsSwitcher(screen as NavigationScreenOrgSettings, submatch, location);
            },
        },
        {
            path: '/o/:org/invite',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'org_invite',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/o/:org/sites/:site/preview/auth',
            onMatch: (submatch: PathMatch<{ org: string; site: string }>) => {
                Object.assign(screen, {
                    type: 'site_preview_auth',
                    organization: submatch.params.org,
                    site: submatch.params.site,
                    redirect: location.query.get('redirect') || undefined,
                });
            },
        },
        {
            path: '/o/:org/sites/:site/members/invite',
            onMatch: (submatch: PathMatch<{ org: string; site: string }>) => {
                Object.assign(screen, {
                    type: 'site_invite',
                    organization: submatch.params.org,
                    site: submatch.params.site,
                });
            },
        },
        {
            path: '/o/:org/sites/:site/agent/:conversation?',
            onMatch: (
                submatch: PathMatch<{
                    org: string;
                    site: string;
                    conversation?: string;
                }>
            ) => {
                const agentScreen: NavigationScreenAgentState = {
                    type: 'agent',
                    organization: submatch.params.org,
                    site: submatch.params.site,
                    ...(submatch.params.conversation
                        ? { conversation: submatch.params.conversation }
                        : {}),
                };
                switcher(
                    location.pathname,
                    [
                        {
                            path: '/~/gitsync',
                            onMatch: (sideSheetMatch) => {
                                agentScreen.sideSheet = { type: 'gitsync' };
                                sideSheetTabSwitcher(
                                    agentScreen,
                                    sideSheetMatch,
                                    location,
                                    'gitsync'
                                );
                            },
                        },
                    ],
                    { prefix: submatch.path }
                );
                Object.assign(screen, agentScreen);
            },
        },
        {
            path: '/sites/:site',
            onMatch: (submatch: PathMatch<{ site: string }>) => {
                Object.assign(screen, {
                    type: 'site',
                    site: submatch.params.site,
                });

                siteSwitcher(screen as NavigationScreenSiteState, submatch, location);
            },
        },
        {
            path: '/o/:org/sites/:site/s/:space',
            onMatch: (submatch: PathMatch<{ org: string; site: string; space: string }>) => {
                Object.assign(screen, {
                    type: 'space',
                    space: submatch.params.space,
                    organization: submatch.params.org,
                    site: submatch.params.site,
                    page: '',
                });

                spaceSwitcher(screen as NavigationScreenSpaceState, submatch, location);
            },
        },
        {
            path: '/o/:org/sites/:site/change-requests',
            onMatch: (submatch: PathMatch<{ org: string; site: string }>) => {
                const changeRequestsScreen: NavigationScreenChangeRequestsState = {
                    type: 'change_requests',
                    organization: submatch.params.org,
                    site: submatch.params.site,
                    filters: extractOptionalChangeRequestsFilterParams(location),
                };
                Object.assign(screen, changeRequestsScreen);
            },
        },
        {
            path: '/o/:org/sites/:site',
            onMatch: (submatch: PathMatch<{ org: string; site: string }>) => {
                Object.assign(screen, {
                    type: 'site',
                    site: submatch.params.site,
                    organization: submatch.params.org,
                });

                siteSwitcher(screen as NavigationScreenSiteState, submatch, location);
            },
        },
        {
            path: '/sites',
            onMatch: () => {
                Object.assign<NavigationScreenState, NavigationScreenSitesListState>(
                    screen,
                    getSitesParams(location)
                );
            },
        },
        {
            path: '/o/:org/sites',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign<NavigationScreenState, NavigationScreenSitesListState>(screen, {
                    ...getSitesParams(location),
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/s/:space',
            onMatch: (submatch: PathMatch<{ space: string }>) => {
                Object.assign(screen, {
                    type: 'space',
                    space: submatch.params.space,
                    page: '',
                });

                spaceSwitcher(screen as NavigationScreenSpaceState, submatch, location);
            },
        },
        {
            path: '/o/:org/s/:space',
            onMatch: (submatch: PathMatch<{ org: string; space: string }>) => {
                Object.assign(screen, {
                    type: 'space',
                    space: submatch.params.space,
                    organization: submatch.params.org,
                    page: '',
                });

                spaceSwitcher(screen as NavigationScreenSpaceState, submatch, location);
            },
        },
        {
            path: '/o/:org/c/:collection',
            onMatch: (submatch: PathMatch<{ org: string; collection: string }>) => {
                Object.assign(screen, {
                    type: 'collection',
                    collection: submatch.params.collection,
                    organization: submatch.params.org,
                    page: '',
                });

                collectionSideSheetSwitcher(
                    screen as NavigationScreenCollectionState,
                    submatch,
                    location
                );
            },
        },
        // Change requests
        {
            path: '/o/:org/change-requests',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const changeRequestsScreen: NavigationScreenChangeRequestsState = {
                    type: 'change_requests',
                    organization: submatch.params.org,
                    filters: extractOptionalChangeRequestsFilterParams(location),
                };
                Object.assign(screen, changeRequestsScreen);
            },
        },

        // Agents
        {
            path: '/o/:org/agents',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const agentsListScreen: NavigationScreenAgentsState = {
                    type: 'agents',
                    organization: submatch.params.org,
                };
                Object.assign(screen, agentsListScreen);
            },
        },

        {
            path: '/o/:org/trash',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'trash',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/o/:org/home',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'home',
                    organization: submatch.params.org,
                });
            },
        },
        {
            path: '/o/:org/content',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const organizationContentScreen: NavigationScreenOrganizationContentState = {
                    type: 'org_content',
                    organization: submatch.params.org,
                };
                Object.assign(screen, organizationContentScreen);
            },
        },
        {
            path: '/o/:org/styleguides',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                const styleguidesScreen: NavigationScreenStyleguidesState = {
                    type: 'styleguides',
                    organization: submatch.params.org,
                };
                Object.assign(screen, styleguidesScreen);
            },
        },
        {
            path: '/o/:org',
            onMatch: (submatch: PathMatch<{ org: string }>) => {
                Object.assign(screen, {
                    type: 'root',
                    organization: submatch.params.org,
                });
            },
        },

        // Welcome
        {
            path: '/welcome',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                });
            },
        },
        {
            path: '/welcome/o/business',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                    organizationType: 'business',
                });
            },
        },
        {
            path: '/welcome/o/community',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'welcome',
                    organizationType: 'community',
                });
            },
        },
        {
            path: '/c/:collection',
            onMatch: (submatch: PathMatch<{ collection: string }>) => {
                Object.assign(screen, {
                    type: 'collection',
                    collection: submatch.params.collection,
                });

                collectionSideSheetSwitcher(
                    screen as NavigationScreenCollectionState,
                    submatch,
                    location
                );
            },
        },

        // Root, Join & home
        {
            path: '/home',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'home',
                });
            },
        },
        {
            path: '/join',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'join',
                });
            },
        },
        {
            path: '/',
            exact: true,
            onMatch: () => {
                Object.assign(screen, {
                    type: 'root',
                });
            },
        },
        {
            path: '/',
            onMatch: () => {
                Object.assign(screen, {
                    type: 'error_notfound',
                });
            },
        },
    ]);
}

/**
 * Sub-switcher for the integration screen
 */
function integrationSettingsTabSwitcher(
    tab: Extract<NavigationScreenOrgSettings['tab'], { type: 'integrations' }>,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    const matchTab = (tabName: string) => {
        const sanitizedTab = findInArray(tabName, ['about', 'installations'] as const);
        tab.tab = sanitizedTab ?? 'about';
    };

    const matchInstallationFlow = (installationState: {
        step: string;
        context?: {
            type: string;
            id: string;
        };
    }) => {
        // installation flow initial step
        const step = findInArray(installationState?.step, ['flow', 'configure'] as const);
        const stepState = { step: step ?? 'flow' };
        let contextState = {};

        // installation flow context (optional)
        if (installationState?.context) {
            const contextType = findInArray(installationState.context.type, [
                'site',
                'space',
            ] as const);

            if (contextType) {
                contextState = {
                    context: {
                        type: contextType,
                        id: installationState.context.id,
                    },
                };
            }
        }

        tab.installation = {
            ...stepState,
            ...contextState,
        };
    };

    switcher(
        location.pathname,
        [
            {
                /**
                 * Installation flow URL
                 *  -- installationStep: flow | configure
                 *  -- contextType: site | space
                 *  -- contextId: siteId | spaceId
                 */
                path: '/:tab?/~/installation/:installationStep?/:contextType?/:contextId?',
                onMatch: (
                    submatch: PathMatch<{
                        tab: string;
                        installationStep: string;
                        contextType: string;
                        contextId: string;
                    }>
                ) => {
                    matchTab(submatch.params.tab);
                    matchInstallationFlow({
                        step: submatch.params.installationStep,
                        context: {
                            type: submatch.params.contextType,
                            id: submatch.params.contextId,
                        },
                    });
                },
            },
            {
                path: '/:tab',
                onMatch: (submatch: PathMatch<{ tab: string; installationStep: string }>) => {
                    matchTab(submatch.params.tab);
                },
            },
            {
                path: '',
                onMatch: () => {
                    tab.tab = 'about';
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for the integrations modal.
 */
function integrationsModalSwitcher(
    screen: NavigationScreenSiteState | NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/installations',
                onMatch: () => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'installations',
                        },
                    };
                },
            },
            {
                path: '/category/:category',
                onMatch: (submatch: PathMatch<{ category: IntegrationCategory | undefined }>) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'category',
                            category: parseIntegrationCategoryParam(submatch.params.category),
                            /** Search query */
                            search: location.query.get('search') || undefined,
                            /** Provider of the integration: marketplace or current org */
                            provider: parseNavigationIntegrationsProviderParam(
                                location.query.get('provider')
                            ),
                        },
                    };
                },
            },
            {
                path: '/:integration/:state',
                onMatch: (
                    submatch: PathMatch<{ integration: string; state: 'configure' | undefined }>
                ) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'integration',
                            integration: submatch.params.integration,
                            state: parseIntegrationModalStateParam(submatch.params.state),
                        },
                    };
                },
            },
            {
                path: '/:integration',
                onMatch: (submatch: PathMatch<{ integration: string }>) => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: { type: 'integration', integration: submatch.params.integration },
                    };
                },
            },
            {
                path: '',
                onMatch: () => {
                    screen.sideSheet = {
                        type: 'integrations',
                        view: {
                            type: 'installations',
                        },
                    };
                },
            },
        ],
        { prefix: match.path }
    );
}

function parseRisonFilters(location: ParsedLocationDescriptor): object | undefined {
    const raw = location.query.get('filters');
    if (!raw) {
        return undefined;
    }
    try {
        return rison.decode_object(raw);
    } catch {
        return undefined;
    }
}

/**
 * Sub-switcher to match a site.
 */
function siteSwitcher(
    screen: NavigationScreenSiteState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    siteSideSheetSwitcher(screen, match, location);
    switcher(
        location.pathname,
        [
            // Before `/analytics/:report?`: the switcher matches on a prefix, so the shorter
            // route would swallow these paths.
            {
                path: '/analytics/:report/questions/:question/answers/:answer',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report:
                            findInArray(submatch.params.report, NavigationSiteInsightsReports) ??
                            'traffic',
                        question: ensureIsDefined(submatch.params.question),
                        answer: ensureIsDefined(submatch.params.answer),
                        filters: parseRisonFilters(location),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/analytics/:report/topics/:topic',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report:
                            findInArray(submatch.params.report, NavigationSiteInsightsReports) ??
                            'traffic',
                        topic: ensureIsDefined(submatch.params.topic),
                        filters: parseRisonFilters(location),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/analytics/:report/questions/:question',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report:
                            findInArray(submatch.params.report, NavigationSiteInsightsReports) ??
                            'traffic',
                        question: ensureIsDefined(submatch.params.question),
                        filters: parseRisonFilters(location),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                /**
                 * @deprecated
                 * Broken URLs moved from its own analytics tab into the Link audit analysis
                 * view. Kept for old links; before `/analytics/:report?`, which would
                 * otherwise swallow this path.
                 */
                path: '/analytics/urls',
                onMatch: () => {
                    const analyticsFilters = parseRisonFilters(location);
                    Object.assign(screen, {
                        type: 'link_audit',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'analysis',
                        ...(analyticsFilters ? { analyticsFilters } : {}),
                    } satisfies NavigationScreenLinkAuditState);
                },
            },
            {
                path: '/analytics/:report?',
                onMatch: (submatch) => {
                    const filters = parseRisonFilters(location);
                    screen.tab = {
                        type: 'analytics',
                        report:
                            findInArray(submatch.params.report, NavigationSiteInsightsReports) ??
                            'traffic',
                        filters,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                /**
                 * @deprecated
                 * The insights screen was folded into the analytics reports. Kept for old links:
                 * its questions and topics open in the AI Assistant report, its overview in Traffic.
                 */
                path: '/insights/topics/:topic',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        topic: ensureIsDefined(submatch.params.topic),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/insights/questions/:question/answers/:answer',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        question: ensureIsDefined(submatch.params.question),
                        answer: ensureIsDefined(submatch.params.answer),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/insights/questions/:question',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        question: ensureIsDefined(submatch.params.question),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/insights/questions',
                onMatch: (submatch) => {
                    screen.tab = { type: 'analytics', report: 'assistant' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/insights/:report',
                onMatch: (submatch) => {
                    const report = findInArray(
                        submatch.params.report,
                        NavigationSiteInsightsReports
                    );

                    if (!report) {
                        return;
                    }

                    const rawFilters = location.query.get('filters');
                    let filters: any;

                    try {
                        filters = rawFilters ? rison.decode_object(rawFilters) : undefined;
                    } catch (_error) {}

                    screen.tab = {
                        type: 'analytics',
                        report,
                        filters,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/insights',
                onMatch: (submatch) => {
                    screen.tab = { type: 'analytics', report: 'traffic' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/new-insights/topics/:topic',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        topic: ensureIsDefined(submatch.params.topic),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/new-insights/questions/:question/answers/:answer',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        question: ensureIsDefined(submatch.params.question),
                        answer: ensureIsDefined(submatch.params.answer),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/new-insights/questions/:question',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'analytics',
                        report: 'assistant',
                        question: ensureIsDefined(submatch.params.question),
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/new-insights/questions',
                onMatch: (submatch) => {
                    screen.tab = { type: 'analytics', report: 'assistant' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/new-insights',
                onMatch: (submatch) => {
                    screen.tab = { type: 'analytics', report: 'traffic' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/triage/scans/:scan',
                onMatch: (submatch) => {
                    const scanFilters = extractSiteScansFilterParams(location);
                    Object.assign(screen, {
                        type: 'triage',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'scans',
                        scan: submatch.params.scan,
                        ...(Object.keys(scanFilters).length ? { scanFilters } : {}),
                    } satisfies NavigationScreenTriageState);
                },
            },
            {
                path: '/triage/scans',
                onMatch: () => {
                    const scanFilters = extractSiteScansFilterParams(location);
                    Object.assign(screen, {
                        type: 'triage',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'scans',
                        ...(Object.keys(scanFilters).length ? { scanFilters } : {}),
                    } satisfies NavigationScreenTriageState);
                },
            },
            {
                path: '/triage/settings',
                onMatch: () => {
                    Object.assign(screen, {
                        type: 'triage',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'settings',
                    } satisfies NavigationScreenTriageState);
                },
            },
            {
                path: '/triage/:finding',
                onMatch: (submatch) => {
                    const filters = extractSiteTriageFilterParams(location);
                    Object.assign(screen, {
                        type: 'triage',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        finding: submatch.params.finding,
                        ...(Object.keys(filters).length ? { filters } : {}),
                    } satisfies NavigationScreenTriageState);
                },
            },
            {
                path: '/triage',
                onMatch: () => {
                    const filters = extractSiteTriageFilterParams(location);
                    Object.assign(screen, {
                        type: 'triage',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        ...(Object.keys(filters).length ? { filters } : {}),
                    } satisfies NavigationScreenTriageState);
                },
            },
            {
                path: '/content-gaps/settings',
                onMatch: () => {
                    Object.assign(screen, {
                        type: 'content_gaps',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'settings',
                    } satisfies NavigationScreenContentGapsState);
                },
            },
            {
                path: '/content-gaps/:finding',
                onMatch: (submatch) => {
                    const filters = extractSiteTriageFilterParams(location);
                    Object.assign(screen, {
                        type: 'content_gaps',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        finding: submatch.params.finding,
                        ...(Object.keys(filters).length ? { filters } : {}),
                        ...extractSiteFindingsOrderByParam(location),
                    } satisfies NavigationScreenContentGapsState);
                },
            },
            {
                path: '/content-gaps',
                onMatch: () => {
                    const filters = extractSiteTriageFilterParams(location);
                    Object.assign(screen, {
                        type: 'content_gaps',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        ...(Object.keys(filters).length ? { filters } : {}),
                        ...extractSiteFindingsOrderByParam(location),
                    } satisfies NavigationScreenContentGapsState);
                },
            },
            {
                // Before `/link-audit/:finding`, which would otherwise swallow the path.
                path: '/link-audit/analysis',
                onMatch: () => {
                    const analyticsFilters = parseRisonFilters(location);
                    Object.assign(screen, {
                        type: 'link_audit',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'analysis',
                        ...(analyticsFilters ? { analyticsFilters } : {}),
                    } satisfies NavigationScreenLinkAuditState);
                },
            },
            {
                // Before `/link-audit/:finding`, which would otherwise swallow the path.
                path: '/link-audit/settings',
                onMatch: () => {
                    Object.assign(screen, {
                        type: 'link_audit',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        view: 'settings',
                    } satisfies NavigationScreenLinkAuditState);
                },
            },
            {
                path: '/link-audit/:finding',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        type: 'link_audit',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        finding: submatch.params.finding,
                        ...extractLinkAuditListParams(location),
                    } satisfies NavigationScreenLinkAuditState);
                },
            },
            {
                path: '/link-audit',
                onMatch: () => {
                    Object.assign(screen, {
                        type: 'link_audit',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                        ...extractLinkAuditListParams(location),
                    } satisfies NavigationScreenLinkAuditState);
                },
            },
            {
                path: '/styleguide/new',
                onMatch: () => {
                    Object.assign(screen, {
                        type: 'styleguide_new',
                        organization: ensureIsDefined(screen.organization),
                        site: screen.site,
                    } satisfies NavigationScreenStyleguideNewState);
                },
            },
            {
                path: '/settings/ai',
                onMatch: (submatch) => {
                    screen.tab = { type: 'customization', tab: 'ai' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/settings/:section',
                onMatch: (submatch) => {
                    const section = findInArray(
                        submatch.params.section,
                        NavigationSiteSettingsSections
                    );
                    screen.tab = {
                        type: 'settings',
                        section: section ?? 'general',
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/preview',
                onMatch: (submatch) => {
                    screen.tab = { type: 'preview' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/structure',
                onMatch: (submatch) => {
                    screen.tab = {
                        type: 'structure',
                        selected: location.query.get('selected') || undefined,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/members',
                onMatch: (submatch) => {
                    screen.tab = { type: 'members' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/customization/v/:siteSpaceId/:tab',
                onMatch: (submatch) => {
                    const tab = findInArray(submatch.params.tab, NavigationSiteCustomizationTabs);
                    screen.tab = {
                        type: 'customization',
                        tab: tab ?? 'general',
                        siteSpaceId: submatch.params.siteSpaceId,
                    };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/customization/:tab',
                onMatch: (submatch) => {
                    const tab = findInArray(submatch.params.tab, NavigationSiteCustomizationTabs);
                    screen.tab = { type: 'customization', tab: tab ?? 'general' };
                    siteSideSheetSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );

    // Extract any params within the Site scope
    const billingParam = findInArray(
        location.query.get('billing'),
        Object.values(NavigationBillingQueryParams)
    );

    if (billingParam) {
        screen.billing = billingParam;
    }
}

function siteSideSheetSwitcher(
    screen: NavigationScreenSiteState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/integrations',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'integrations' };
                    integrationsModalSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/gitsync',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'gitsync' };
                    sideSheetTabSwitcher(screen, submatch, location, 'gitsync');
                },
            },
            {
                path: '/~/audience',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'audience' };
                    sideSheetTabSwitcher(screen, submatch, location, 'audience');
                },
            },
            {
                path: '/~/publish',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'publish' };
                    sideSheetTabSwitcher(screen, submatch, location, 'publish');
                },
            },
            {
                path: '/~/published',
                onMatch: (submatch) => {
                    screen.sideSheet = { type: 'published' };
                    sideSheetTabSwitcher(screen, submatch, location, 'published');
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match a space.
 */
function spaceSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/diff',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'diff',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/preview',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'preview',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/edit',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'edit',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/checks',
                onMatch: (submatch) => {
                    Object.assign(screen, {
                        mode: 'checks',
                    });

                    spaceContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    spaceContentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );

    // Ensure default mode is set
    ensureSpaceScreenMode(screen);
}

/**
 * Sub-switcher to the content at a space or change request level.
 */
function contentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/revisions/:baseRevision...:revision',
                onMatch: (submatch: PathMatch<{ baseRevision: string; revision: string }>) => {
                    Object.assign(screen, {
                        baseRevision: submatch.params.baseRevision,
                        revision: parseRevision(submatch.params.revision),
                    });

                    reusableContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/revisions/:revision',
                onMatch: (submatch: PathMatch<{ revision: string }>) => {
                    Object.assign(screen, {
                        revision: parseRevision(submatch.params.revision),
                    });

                    reusableContentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch: PathMatch<{ changeRequest: string }>) => {
                    reusableContentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for reusable content of a space.
 */
/**
 * Sub-switcher to the content at a space or change request level.
 */
function reusableContentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: `/~/reusable/:reusable/:page${MATCH_PAGE}`,
                onMatch: (submatch: PathMatch<{ reusable: string; page: string }>) => {
                    Object.assign(screen, {
                        reusableContent: submatch.params.reusable,
                        page: submatch.params.page,
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: '/~/reusable/:reusable',
                onMatch: (submatch: PathMatch<{ reusable: string }>) => {
                    Object.assign(screen, {
                        reusableContent: submatch.params.reusable,
                        page: '',
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: `/:page${MATCH_PAGE}`,
                onMatch: (submatch: PathMatch<{ page: string }>) => {
                    Object.assign(screen, {
                        page: submatch.params.page,
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch: PathMatch) => {
                    Object.assign(screen, {
                        page: '',
                    });

                    spaceCommentsSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher for the content of space.
 */
function spaceContentSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/~/changes/:changeRequest',
                onMatch: (submatch: PathMatch<{ changeRequest: string }>) => {
                    const change = location.query.get('change');
                    Object.assign(screen, {
                        changeRequest: parseChangeRequestIdentifier(submatch.params.changeRequest),
                        ...(change ? { change } : {}),
                    });

                    contentSwitcher(screen, submatch, location);
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    contentSwitcher(screen, submatch, location);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match a comments side-panel.
 */
function commentsSwitcher(
    match: PathMatch,
    location: ParsedLocationDescriptor,
    onMatch: (sideSheet: NavigationCommentsSideSheet) => void,
    onFallbackMatch: (submatch: PathMatch) => void
): void {
    switcher(
        location.pathname,
        [
            /**
             * @deprecated
             * This is a legacy route that we need to support for backwards compatibility
             */
            {
                path: '/~/c/:node/:comment?',
                onMatch: (submatch: PathMatch<{ node: string; comment?: string }>) => {
                    onMatch({
                        type: 'comments',
                        node: submatch.params.node,
                        comment: submatch.params.comment,
                    });
                },
            },
            /**
             * @deprecated
             * This is a legacy route that we need to support for backwards compatibility
             */
            {
                path: '/~/comments/:tab/~c/:comment',
                onMatch: (submatch: PathMatch<{ comment: string }>) => {
                    onMatch({
                        type: 'comments',
                        comment: submatch.params.comment,
                    });
                },
            },
            {
                path: '/~/comments/:comment?',
                onMatch: (submatch: PathMatch<{ comment?: string }>) => {
                    const comment = submatch.params.comment;
                    const context = findInArray(location.query.get('context'), [
                        'preview',
                    ] as const);
                    const node = location.query.get('node');
                    const record = location.query.get('record');
                    const definition = location.query.get('definition');

                    onMatch({
                        type: 'comments',
                        ...(comment ? { comment } : {}),
                        ...(context ? { context } : {}),
                        ...(node ? { node } : {}),
                        ...(record && definition ? { tableCell: { record, definition } } : {}),
                    });
                },
            },
            {
                path: '',
                onMatch: (submatch) => {
                    onFallbackMatch(submatch);
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Match comments side-sheet in a space
 */
function spaceCommentsSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
) {
    commentsSwitcher(
        match,
        location,
        (sideSheet) => {
            screen.sideSheet = sideSheet;
        },
        (submatch) => {
            spaceSideSheetSwitcher(screen, submatch, location);
        }
    );
}

/**
 * Sub-switcher to match the sidesheet in a collection
 */
function collectionSideSheetSwitcher(
    screen: NavigationScreenCollectionState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        COLLECTION_SIDESHEET_KEYS.map((sideSheet) => ({
            path: `/~/${sideSheet}`,
            onMatch: (submatch) => {
                screen.sideSheet = { type: sideSheet };

                sideSheetTabSwitcher(screen, submatch, location, sideSheet);
            },
        })),
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match the sidesheet in a space
 */
function spaceSideSheetSwitcher(
    screen: NavigationScreenSpaceState,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    const keys = getSpaceSideSheetKeys(screen);

    switcher(
        location.pathname,
        keys.map((sideSheet) => ({
            path: `/~/${sideSheet}`,
            onMatch: (submatch: PathMatch) => {
                screen.sideSheet = { type: sideSheet };

                if (sideSheet === 'integrations') {
                    integrationsModalSwitcher(screen, submatch, location);
                } else if (sideSheet === 'change-requests') {
                    changeRequestsSideSheetSwitcher(
                        screen.sideSheet as NavigationChangeRequestsSideSheet,
                        submatch,
                        location
                    );
                } else {
                    sideSheetTabSwitcher(screen, submatch, location, sideSheet);
                }
            },
        })),
        { prefix: match.path }
    );
}

function changeRequestsSideSheetSwitcher(
    sideSheet: NavigationChangeRequestsSideSheet,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    // Backwards compatibility to support old shared URLs that encoded a single status as
    // `/~/change-requests/:status`. As opposed to the new multiple query params.
    const legacyStatus = switcher(
        location.pathname,
        [
            {
                path: '/:status',
                onMatch: (submatch: PathMatch<{ status: string }>) => {
                    return parseChangeRequestStatus(submatch.params.status);
                },
            },
        ],
        { prefix: match.path }
    );

    sideSheet.filters = extractChangeRequestsSideSheetFilterParams(location, legacyStatus);
}

/**
 * Sub-switcher to match the tab of a sidesheet
 */
function sideSheetTabSwitcher(
    screen:
        | NavigationScreenSpaceState
        | NavigationScreenCollectionState
        | NavigationScreenSiteState
        | NavigationScreenAgentState,
    match: PathMatch,
    location: ParsedLocationDescriptor,
    sideSheetKey: NavigationSideSheetType
): void {
    const tabs = getNavigationSideSheetTabs(sideSheetKey);

    if (tabs.length) {
        switcher(
            location.pathname,
            tabs
                .map((tab) =>
                    tab
                        ? {
                              path: `/${tab}`,
                              onMatch: () => {
                                  screen.sideSheet = screen.sideSheet
                                      ? Object.assign(screen.sideSheet, { tab })
                                      : undefined;
                              },
                          }
                        : undefined
                )
                .filter(filterOutNullable),
            { prefix: match.path }
        );
    }
}

/**
 * Sub-switcher to match the tabs in account settings
 */
function accountSettingsSwitcher(
    screen: NavigationScreenAccountSettings,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/developer/:developerTab',
                onMatch: (developerMatch: PathMatch<{ developerTab: string }>) => {
                    screen.tab = 'developer';
                    screen.developerTab = NavigationAccountDeveloperTabSchema.safeParse(
                        developerMatch.params.developerTab
                    ).data;
                },
            },
            {
                path: '/profile',
                onMatch: () => {
                    screen.tab = 'profile';
                },
            },
            {
                path: '/developer',
                onMatch: () => {
                    screen.tab = 'developer';
                },
            },
            {
                path: '/organizations',
                onMatch: () => {
                    screen.tab = 'organizations';
                },
            },
            {
                path: '/notification',
                onMatch: () => {
                    screen.tab = 'notification';
                },
            },
        ],
        { prefix: match.path }
    );
}

/**
 * Sub-switcher to match the tabs in organization settings
 */
function orgSettingsSwitcher(
    screen: NavigationScreenOrgSettings,
    match: PathMatch,
    location: ParsedLocationDescriptor
): void {
    switcher(
        location.pathname,
        [
            {
                path: '/integrations/:integration',
                onMatch: (submatch: PathMatch<{ integration: string }>) => {
                    const tab: Extract<
                        NavigationScreenOrgSettings['tab'],
                        { type: 'integrations' }
                    > = {
                        type: 'integrations',
                        integration: submatch.params.integration,
                        tab: 'about',
                    };
                    screen.tab = tab;
                    integrationSettingsTabSwitcher(tab, submatch, location);
                },
            },
            {
                path: '/integrations',
                onMatch: () => {
                    screen.tab = {
                        type: 'integrations',
                        category: parseIntegrationCategoryParam(location.query.get('category')),
                        search: location.query.get('search') || undefined,
                        provider: parseNavigationIntegrationsProviderParam(
                            location.query.get('provider')
                        ),
                    };
                },
            },
            {
                path: '/openapi/:spec',
                onMatch: (submatch: PathMatch<{ spec: string }>) => {
                    screen.tab = {
                        type: 'openapi',
                        spec: submatch.params.spec,
                    };
                },
            },
            {
                path: '/openapi',
                onMatch: () => {
                    screen.tab = { type: 'openapi' };
                },
            },
            {
                path: '/translations/glossary',
                onMatch: () => {
                    screen.tab = {
                        type: 'translations',
                        view: 'glossary',
                    };
                },
            },
            {
                path: '/translations',
                onMatch: () => {
                    screen.tab = {
                        type: 'translations',
                        view: 'overview',
                    };
                },
            },
            {
                path: '/audit-logs/:eventId',
                onMatch: (submatch: PathMatch<{ eventId: string }>) => {
                    screen.tab = {
                        type: 'audit-logs',
                        eventId: submatch.params.eventId,
                    };
                },
            },
            {
                path: '/audit-logs',
                onMatch: () => {
                    screen.tab = { type: 'audit-logs' };
                },
            },
            {
                path: '/members/:memberId/:detailsTab',
                onMatch: (submatch: PathMatch<{ memberId: string; detailsTab: string }>) => {
                    const memberId = submatch.params.memberId;
                    const detailsTab = submatch.params.detailsTab;

                    screen.tab = {
                        type: 'members',
                        memberId,
                        detailsTab,
                    };
                },
            },
            {
                path: '/members/:memberId?',
                onMatch: (submatch: PathMatch<{ memberId?: string }>) => {
                    const memberId = submatch.params.memberId;

                    screen.tab = {
                        type: 'members',
                        memberId,
                    };
                },
            },
            {
                path: '/teams/:teamId/:detailsTab',
                onMatch: (submatch: PathMatch<{ teamId: string; detailsTab: string }>) => {
                    const teamId = submatch.params.teamId;
                    const detailsTab = submatch.params.detailsTab;

                    screen.tab = {
                        type: 'teams',
                        teamId,
                        detailsTab,
                    };
                },
            },
            {
                path: '/teams/:teamId?',
                onMatch: (submatch: PathMatch<{ teamId?: string }>) => {
                    const teamId = submatch.params.teamId;

                    screen.tab = {
                        type: 'teams',
                        teamId,
                    };
                },
            },
            {
                path: '/sso',
                onMatch: () => {
                    screen.tab = { type: 'sso' };
                },
            },
            {
                path: '/invites',
                onMatch: () => {
                    screen.tab = { type: 'invites' };
                },
            },
            {
                path: '/rules',
                onMatch: () => {
                    screen.tab = { type: 'rules' };
                },
            },
            {
                path: '/agents/:tab?',
                onMatch: (submatch: PathMatch<{ tab?: string }>) => {
                    screen.tab = {
                        type: 'agents',
                        tab: parseAgentSettingsTab(submatch.params.tab),
                    };
                },
            },
            {
                path: '/plans',
                onMatch: () => {
                    const rawInterval = location.query.get('interval');
                    screen.tab = {
                        type: 'plans',
                        interval:
                            rawInterval === 'monthly' || rawInterval === 'yearly'
                                ? rawInterval
                                : undefined,
                    };
                },
            },
            {
                path: '',
                onMatch: () => {
                    screen.tab = { type: 'profile' };
                },
            },
        ],
        { prefix: match.path }
    );
}

const AGENT_SETTINGS_TABS = ['settings'] as const;
type AgentSettingsTab = (typeof AGENT_SETTINGS_TABS)[number];

function parseAgentSettingsTab(rawTab?: string | null): AgentSettingsTab {
    return (AGENT_SETTINGS_TABS as readonly string[]).includes(rawTab ?? '')
        ? (rawTab as AgentSettingsTab)
        : 'settings';
}

function extractChangeRequestsFilterParams(
    location: ParsedLocationDescriptor,
    legacyStatus?: api.ChangeRequestStatus
): NavigationScreenChangeRequestsFilters {
    const queryStatuses = Array.from(
        new Set(
            location.query.getAll('status').flatMap((status) => {
                const validStatus = parseChangeRequestStatus(status);
                return validStatus ? [validStatus] : [];
            })
        )
    );
    const statuses = queryStatuses.length ? queryStatuses : legacyStatus ? [legacyStatus] : [];
    const contributor = location.query.get('contributor') ?? undefined;
    const requestedReview = location.query.get('requestedReview') ?? undefined;
    const creator = location.query.get('creator') ?? undefined;
    const space = location.query.get('space') ?? undefined;
    const finding = findInArray(location.query.get('finding'), ['none', 'all'] as const);

    return {
        ...(statuses.length ? { status: statuses } : {}),
        ...(contributor ? { contributor } : {}),
        ...(creator ? { creator } : {}),
        ...(requestedReview ? { requestedReview } : {}),
        ...(space ? { space } : {}),
        ...(finding ? { finding } : {}),
    };
}

function extractOptionalChangeRequestsFilterParams(
    location: ParsedLocationDescriptor
): NavigationScreenChangeRequestsFilters | undefined {
    const filters = extractChangeRequestsFilterParams(location);
    return Object.keys(filters).length ? filters : undefined;
}

function extractChangeRequestsSideSheetFilterParams(
    location: ParsedLocationDescriptor,
    legacyStatus?: api.ChangeRequestStatus
): NavigationChangeRequestsSideSheet['filters'] {
    const { space: _space, ...filters } = extractChangeRequestsFilterParams(location, legacyStatus);
    return Object.keys(filters).length ? filters : undefined;
}

function parseChangeRequestStatus(
    status: string | null | undefined
): api.ChangeRequestStatus | undefined {
    return isInArray(status, Object.values(api.ChangeRequestStatus)) ? status : undefined;
}

function extractSiteTriageFilterParams(location: ParsedLocationDescriptor): {
    topic?: string;
    severity?: api.SiteFindingSeverity;
    status?: api.SiteFindingStatus;
    type?: api.SiteFindingType;
    source?: string[];
    hasChangeRequests?: boolean;
    dateRange?: NavigationScreenSiteFindingsDateRange;
    state?: api.SiteFindingState;
} {
    const topic = location.query.get('topic') ?? undefined;
    const status = findInArray(location.query.get('status'), Object.values(api.SiteFindingStatus));
    const severity = findInArray(
        location.query.get('severity'),
        Object.values(api.SiteFindingSeverity)
    );
    const type = findInArray(location.query.get('type'), Object.values(api.SiteFindingType));
    const source = location.query.getAll('source');
    const hasChangeRequestsParam = location.query.get('hasChangeRequests');
    const hasChangeRequests =
        hasChangeRequestsParam === 'true'
            ? true
            : hasChangeRequestsParam === 'false'
              ? false
              : undefined;
    const dateRange = findInArray(
        location.query.get('dateRange'),
        NavigationScreenSiteFindingsDateRanges
    );
    const state = findInArray(location.query.get('state'), Object.values(api.SiteFindingState));

    return {
        ...(topic ? { topic } : {}),
        ...(status ? { status } : {}),
        ...(severity ? { severity } : {}),
        ...(type ? { type } : {}),
        ...(source.length ? { source } : {}),
        ...(hasChangeRequests !== undefined ? { hasChangeRequests } : {}),
        ...(dateRange ? { dateRange } : {}),
        ...(state ? { state } : {}),
    };
}

function extractSiteFindingsOrderByParam(location: ParsedLocationDescriptor): {
    orderBy?: api.SiteFindingOrderBy;
} {
    const orderBy = findInArray(
        location.query.get('orderBy'),
        Object.values(api.SiteFindingOrderBy)
    );

    return orderBy ? { orderBy } : {};
}

/** The link audit list's filters, sort order and URL search, also kept on a finding's URL. */
function extractLinkAuditListParams(
    location: ParsedLocationDescriptor
): Pick<NavigationScreenLinkAuditState, 'filters' | 'orderBy' | 'url'> {
    const filters = extractSiteTriageFilterParams(location);
    const url = location.query.get('url');

    return {
        ...(Object.keys(filters).length ? { filters } : {}),
        ...extractSiteFindingsOrderByParam(location),
        ...(url ? { url } : {}),
    };
}

function extractSiteScansFilterParams(location: ParsedLocationDescriptor): {
    topic?: string;
    status?: api.SiteScanStatus;
} {
    const topic = location.query.get('topic') ?? undefined;
    const status = findInArray(location.query.get('status'), Object.values(api.SiteScanStatus));

    return {
        ...(topic ? { topic } : {}),
        ...(status ? { status } : {}),
    };
}

/**
 * Parse the change request identifier from the url into either an ID or a number.
 */
function parseChangeRequestIdentifier(input: string): string | number {
    if (/^\d+$/.test(input)) {
        return Number.parseInt(input, 10);
    }
    return input;
}
